import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, RefreshCw, CheckCircle, ArrowLeftCircle } from 'lucide-react';
import { PublicHeader, PublicFooter } from '../components/PublicLayout';
import { toast } from 'sonner';
import api from '../services/api';

// Brand colors
const BRAND_BLUE = '#2B4B6A';
const BRAND_ORANGE = '#F58A5A';

const EmailVerificationRequired = () => {
  const { user, logout, isImpersonating, returnToAdmin } = useAuth();
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleResendEmail = async () => {
    setSending(true);
    try {
      await api.post('/auth/resend-verification');
      setSent(true);
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to send verification email. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const handleReturnToAdmin = async () => {
    const success = await returnToAdmin();
    if (success) {
      toast.success('Returned to admin account');
      navigate('/admin');
    } else {
      toast.error('Failed to return to admin');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <PublicHeader />
      
      {/* Return to Admin Banner - shown when impersonating */}
      {isImpersonating && (
        <div className="bg-amber-100 border-b border-amber-300 px-6 py-3">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <span className="text-amber-800 text-sm font-medium">
              You are currently viewing this account as an admin.
            </span>
            <button
              onClick={handleReturnToAdmin}
              data-testid="return-to-admin-btn"
              className="flex items-center gap-2 px-4 py-2 bg-amber-200 text-amber-900 rounded-lg hover:bg-amber-300 text-sm font-medium transition"
            >
              <ArrowLeftCircle className="w-4 h-4" />
              Return to Admin
            </button>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          <div 
            className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: `${BRAND_BLUE}15` }}
          >
            <Mail className="w-10 h-10" style={{ color: BRAND_BLUE }} />
          </div>
          
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">Verify Your Email</h1>
          
          <p className="text-gray-600 mb-6">
            We've sent a verification link to <strong>{user?.email}</strong>. 
            Please check your inbox and click the link to verify your email address.
          </p>

          {sent ? (
            <div className="flex items-center justify-center gap-2 text-green-600 mb-6">
              <CheckCircle className="w-5 h-5" />
              <span>Verification email sent!</span>
            </div>
          ) : (
            <button
              onClick={handleResendEmail}
              disabled={sending}
              className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl text-white font-medium transition-all hover:shadow-lg disabled:opacity-50"
              style={{ backgroundColor: BRAND_BLUE }}
            >
              {sending ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5" />
                  Resend Verification Email
                </>
              )}
            </button>
          )}

          <div className="mt-6 pt-6 border-t border-gray-100">
            <p className="text-sm text-gray-500 mb-3">
              Didn't receive the email? Check your spam folder or try a different email address.
            </p>
            <button
              onClick={logout}
              className="text-sm font-medium hover:underline"
              style={{ color: BRAND_ORANGE }}
            >
              Sign out and use a different email
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
};

export default EmailVerificationRequired;
